import { core } from '@musinsa/pbo-core'
import { OUTBOUND_PROGRESS_DONE_STATUSES } from '@/pages/outbound/constants'

/**
 * 진행률 응답 — BE `GetOutboundProgressUsecase.OutboundProgress`. 일반출고·기타출고가 같은 API 를 쓰지만 서비스 타입은 화면별로 따로 있어,
 * 한쪽에 묶이지 않게 모달이 필요한 모양만 여기 둔다.
 */
interface OutboundProgressView {
  status: string
  processed: number
  total: number
  message: string
}

interface Props {
  open: boolean
  progress: OutboundProgressView | null
  onClose: () => void
}

/**
 * 저장 진행률 모달 — 1.0 `ProgressPollingModal`(grid.js `openProgressModal`) 의 화면 쪽.
 *
 * 표시만 한다. `props.progress`(processed/total·상태)로 퍼센트를 그리고, 끝난 상태에서만 [확인]·ESC 로 닫힌다.
 * 폴링(`GET /api/v1/outbound-progress/{key}`)은 부모 화면이 `useGetOutboundProgress` 로 하고, 열고 닫음은 부모의 `progressKey` state 가 정한다.
 *
 * 2.0 레포의 `ProgressModalContext` 는 iframe(1.0)이 보낸 메시지로 열고 닫는 플래그만 있고 폴링이
 * 없어 재사용할 수 없다. 일반출고(GI31)·기타출고(GI35)가 함께 쓴다(1.0 도 두 화면이 같은 모달 — grid.js:473).
 */
export function OutboundProgressModal({ open, progress, onClose }: Readonly<Props>) {
  const total = progress?.total ?? 0
  const processed = progress?.processed ?? 0
  const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0
  const isSucceeded = progress?.status === 'COMPLETED'
  // 실패도 끝난 상태다. 성공만 종료로 보면 FAILED 일 때 확인 버튼이 잠겨 모달을 닫을 수 없다.
  const isTerminal = OUTBOUND_PROGRESS_DONE_STATUSES.includes(progress?.status ?? '')

  return (
    <core.Modal
      open={open}
      title="작업 진행 중"
      width={420}
      closable={isTerminal}
      // ESC 도 끝난 뒤에만 — 켜 두면 closable 과 상관없이 onCancel 이 불려, 워크플로가 도는 중에 저장·확정 잠금(progressKey)이 풀린다.
      keyboard={isTerminal}
      maskClosable={false}
      okText="확인"
      onOk={onClose}
      onCancel={onClose}
      okButtonProps={{ disabled: !isTerminal }}
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      <core.Flex vertical gap={12} align="stretch">
        <core.Progress percent={percent} status={isSucceeded ? 'success' : isTerminal ? 'exception' : 'active'} />
        <core.Typography.Text type="secondary">
          {total > 0 ? `${processed} / ${total} 건 처리` : '처리 대기 중입니다.'}
        </core.Typography.Text>
        {progress?.message ? <core.Typography.Text>{progress.message}</core.Typography.Text> : null}
      </core.Flex>
    </core.Modal>
  )
}
